import React from "react";
import "./SettingsSection.css";

/**
 * Props for the SettingsSection component
 */
export interface SettingsSectionProps {
  /** The main title of the settings section */
  title: string;
  /** Descriptive text explaining the setting */
  description: string;
  /** Content to display in the section (inputs, forms, etc.) - mutually exclusive with action */
  children?: React.ReactNode;
  /** Action element to display (buttons, etc.) - mutually exclusive with children */
  action?: React.ReactNode;
  /** Additional CSS classes to apply */
  className?: string;
}

/**
 * A reusable settings section component that supports both content and action variants.
 *
 * @example
 * // Content variant with form inputs
 * <SettingsSection
 *   title="Workspace Name"
 *   description="Choose a name for your workspace"
 * >
 *   <input type="text" />
 *   <button>Save</button>
 * </SettingsSection>
 *
 * @example
 * // Action variant with button
 * <SettingsSection
 *   title="Reset History"
 *   description="This will reset your conversation history"
 *   action={<button onClick={handleReset}>Reset</button>}
 * />
 */
const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  description,
  children,
  action,
  className = "",
}) => {
  return (
    <div className={`settings-section ${className}`}>
      <div className="settings-row">
        <div className="settings-header">
          <div className="row">
            <h2>{title}</h2>
          </div>
          <span style={{ color: "var(--text-color-secondary)" }}>
            {description}
          </span>
        </div>
        {action ? (
          <div className="settings-action">{action}</div>
        ) : (
          <div className="settings-content">{children}</div>
        )}
      </div>
    </div>
  );
};

export default SettingsSection;
